"use client";
import {
  ChangeEvent,
  Dispatch,
  FC,
  HTMLProps,
  SetStateAction,
  forwardRef,
  useState,
} from "react";

// libs
import classNames from "classnames";
import { Tooltip } from "react-tooltip";

// components
import { Error, ShowWhen } from "@/components/common";

// assets
import {
  InfoIcon,
  VisibilityOffIcon,
  VisibilityOnIcon,
} from "@/components/assets/icons";
import { useTranslations } from "next-intl";
import { PasswordStrength } from "@/enums";

type InputProps = {
  id?: string;
  label?: string;
  name?: string;
  error?: any;
  value?: string | number;
  type?: string;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  icon?: any;
  infoContent?: any;
  onInfoIconClick?: () => void;
  additionalInfoIcon?: any;
  onIconClick?: () => void;
  currency?: string;
  iconPosition?: "left" | "right";
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  checkPasswordStrength?: boolean;
  setPasswordStrength?: Dispatch<SetStateAction<PasswordStrength>>;
  errorMessageRight?: boolean;
} & HTMLProps<HTMLInputElement>;

function evaluatePasswordStrength(password: string) {
  let score = 0;
  if (!password) return PasswordStrength.NONE;
  // Check password length
  if (password.length > 6) score += 1;
  // Contains lowercase
  if (/[a-z]/.test(password)) score += 1;
  // Contains uppercase
  if (/[A-Z]/.test(password)) score += 1;
  // Contains numbers
  if (/\d/.test(password)) score += 1;
  // Contains special characters
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  switch (score) {
    case 0:
    case 1:
    case 2:
      return PasswordStrength.WEAK;
    case 3:
      return PasswordStrength.MEDIUM;
    case 4:
    case 5:
      return PasswordStrength.STRONG;
  }
}

const Input: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label = "",
      value,
      type = "text",
      name,
      onChange,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      icon,
      infoContent,
      onInfoIconClick,
      additionalInfoIcon,
      onIconClick,
      currency,
      iconPosition = "right",
      checkPasswordStrength = true,
      setPasswordStrength,
      required,
      errorMessageRight,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [strength, setStrength] = useState<PasswordStrength>(
      PasswordStrength.NONE,
    );

    const t = useTranslations();

    const Icon = icon;
    const AdditionalInfoIcon = additionalInfoIcon;

    const inputClasses = classNames(
      "w-full",
      "rounded-[2.25rem]",
      "bg-gray-100",
      "h-[63px]",
      "px-8",
      "border",
      "leading-tight",
      "text-base",
      "font-normal",
      "break-all",
      "focus:outline-none",
      {
        // error
        "border-red-600": !!error,
        // active
        "border-transparent active:border-[#cfe0e2]": !error,
        // disabled
        "disabled:bg-zinc-100 disabled:text-zinc-400 disabled:border-none":
          !error,
        // success
        "border-emerald-600": success,
        "cursor-not-allowed": disabled,
      },
      className,
    );

    const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e);
      if (checkPasswordStrength && type === "password") {
        const strength = evaluatePasswordStrength(e.target.value);
        setStrength(strength);
        if (setPasswordStrength) setPasswordStrength(strength);
      }
    };

    return (
      <div className={classNames("relative w-full", containerClassName)}>
        <div className={classNames("input-wrapper w-full", wrapperClassName)}>
          <input
            required={required}
            id={id}
            ref={ref}
            className={classNames("input-field pt-4", inputClasses)}
            name={name}
            disabled={disabled}
            maxLength={maxLength}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            type={type === "password" ? "password" : type}
            placeholder=""
            onChange={handleChangeInput}
            value={value || ""}
            {...props}
          />
          <ShowWhen condition={type === "password"}>
            <div
              className={classNames(
                "absolute left-[50%] top-full mx-auto mt-[-3px] flex w-[87%] translate-x-[-50%] justify-between pt-[5px]",
                {
                  "border-t-[2px]": !!checkPasswordStrength,
                  "border-transparent": strength === PasswordStrength.NONE,
                  "border-[#e53238]":
                    strength === PasswordStrength.WEAK || error,
                  "border-yellow-500":
                    strength === PasswordStrength.MEDIUM && !error,
                  "border-[#059661]":
                    strength === PasswordStrength.STRONG && !error,
                },
              )}
            >
              <span className="text-xs font-normal text-black opacity-30">
                {showPassword && value}
              </span>
              <span
                className={classNames("text-xs font-normal", {
                  "text-[#e53238]": strength === PasswordStrength.WEAK || error,
                  "text-yellow-500":
                    strength === PasswordStrength.MEDIUM && !error,
                  "text-[#0b9c83]":
                    strength === PasswordStrength.STRONG && !error,
                })}
              >
                {(error && errorMessageRight && t(error)) ||
                  (strength && t(strength))}
              </span>
            </div>
          </ShowWhen>

          <ShowWhen
            condition={
              icon ||
              type === "password" ||
              !!infoContent ||
              !!onInfoIconClick ||
              currency
            }
          >
            <div
              className={classNames(
                "absolute !top-[calc(50%-12px)] flex items-center",
                {
                  "left-[25px]": iconPosition === "left",
                  "right-[25px]": iconPosition === "right",
                  "input-icon-error": infoContent && error,
                  "cursor-pointer": !!onIconClick,
                },
              )}
              onClick={onIconClick}
            >
              <ShowWhen condition={!!currency}>
                <span className="text-base font-bold text-black">
                  {currency}
                </span>
              </ShowWhen>
              {icon && <Icon />}
              <ShowWhen condition={infoContent || !!onInfoIconClick}>
                {infoContent ? (
                  <>
                    {AdditionalInfoIcon ? (
                      <AdditionalInfoIcon
                        data-tooltip-id="my-tooltip"
                        onClick={onInfoIconClick}
                      />
                    ) : (
                      <InfoIcon
                        data-tooltip-id="my-tooltip"
                        onClick={onInfoIconClick}
                      />
                    )}
                    <Tooltip
                      id="my-tooltip"
                      place={AdditionalInfoIcon ? "top" : "right"}
                      className={classNames(
                        AdditionalInfoIcon ? "" : "!left-[67px]",
                        "!rounded-[14px] !bg-white !p-[3px] drop-shadow",
                      )}
                    >
                      {infoContent}
                    </Tooltip>
                  </>
                ) : (
                  <InfoIcon onClick={onInfoIconClick} />
                )}
              </ShowWhen>
              <ShowWhen condition={type === "password"}>
                <ShowWhen
                  condition={showPassword}
                  elseRender={
                    <VisibilityOffIcon
                      className="cursor-pointer"
                      onClick={() => setShowPassword(true)}
                    />
                  }
                >
                  {
                    <VisibilityOnIcon
                      className="cursor-pointer"
                      onClick={() => setShowPassword(false)}
                    />
                  }
                </ShowWhen>
              </ShowWhen>
            </div>
          </ShowWhen>
          <label
            className={classNames(
              "input-label leading-tight",
              labelClassName,
              !!error && "text-red-600 !opacity-100",
            )}
            htmlFor={id}
          >
            {label && t(label)}
          </label>
        </div>
        {error && !errorMessageRight && typeof error === "string" && (
          <Error text={error} className={type === "password" ? "mt-4" : ""} />
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
